import { createSlice, ActionReducerMapBuilder, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchDrivers, updateDriver, addDriver } from '../actions';
import { Driver } from '../types';
import { api } from '../../services/api';

interface DriverState {
  items: Driver[];
  loading: boolean;
  error: string | null;
}

const initialState: DriverState = {
  items: [],
  loading: false,
  error: null,
};

// Create a new action to refresh drivers after adding
export const refreshDriversAfterAdd = createAsyncThunk<Driver[], void>(
  'drivers/refreshAfterAdd',
  async (_, { rejectWithValue }) => {
    try {
      // Clear the cache to ensure fresh data
      await api.clearCache('getDrivers');
      const response = await api.getDrivers();
      
      if (!Array.isArray(response)) {
        console.error('Response is not an array:', response);
        return [];
      }
      
      // Transform the data to match the Driver type
      const drivers = response.map(driver => {
        const firstName = driver.firstName || '';
        const lastName = driver.lastName || '';
        return {
          id: driver.id,
          firstName: firstName,
          lastName: lastName,
          fullName: `${firstName} ${lastName}`,
          email: driver.email || '',
          licenseNumber: driver.licenseNumber || '',
          mobileNumber: driver.mobileNumber || '',
          photoId: driver.photoId || '',
          role: driver.role || '',
          status: driver.status || 'Available',
          originalData: driver
        };
      });
      return drivers;
    } catch (error: any) {
      console.error('Error refreshing drivers after add:', error);
      return rejectWithValue(error.response?.data || 'Failed to refresh drivers');
    }
  }
);

// Delete driver action
export const deleteDriver = createAsyncThunk<{id: number, message: string}, number>(
  'drivers/deleteDriver',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await api.deleteDriver(id);
      // Return both the ID and any message from the response
      return {
        id,
        message: typeof response === 'object' && response && 'message' in response ? String(response.message) : 'Driver deleted successfully'
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to delete driver');
    }
  }
);

const driverSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    clearDriverErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<DriverState>) => {
    // Fetch Drivers
    builder.addCase(fetchDrivers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDrivers.fulfilled, (state, action: PayloadAction<Driver[]>) => {
      state.loading = false;
      state.items = action.payload;
    });
    builder.addCase(fetchDrivers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'An error occurred';
    });

    // Update Driver
    builder.addCase(updateDriver.fulfilled, (state, action: PayloadAction<Driver>) => {
      const index = state.items.findIndex((driver) => driver.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    });
    builder.addCase(updateDriver.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to update driver';
    });

    // Delete Driver
    builder.addCase(deleteDriver.fulfilled, (state, action: PayloadAction<{id: number, message: string}>) => {
      state.items = state.items.filter((driver) => driver.id !== action.payload.id);
      // Success message is available in action.payload.message
    });
    builder.addCase(deleteDriver.rejected, (state, action) => {
      state.error = action.error.message || 'Failed to delete driver';
    });

    // Add Driver
    builder.addCase(addDriver.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addDriver.fulfilled, (state, action: PayloadAction<Driver>) => {
      state.loading = false;
      state.items.push(action.payload);
    });
    builder.addCase(addDriver.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Refresh Drivers After Add
    builder.addCase(refreshDriversAfterAdd.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(refreshDriversAfterAdd.fulfilled, (state, action: PayloadAction<Driver[]>) => {
      state.loading = false;
      state.items = action.payload;
    });
    builder.addCase(refreshDriversAfterAdd.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to refresh drivers';
    });
  },
});

export default driverSlice.reducer;
